/**
 * Level progress for the Foe Elevator: stars per floor, unlocking and the device-local store.
 * The rules are in docs/design/foe-elevator.md.
 */

import { newMastery, updateMastery, type Mastery } from './mastery';

export type Stars = 0 | 1 | 2 | 3;

/** How one mission (a full practice session on a floor) went. */
export interface MissionResult {
  /** Different targets practised. */
  targets: number;
  /** Targets said with a correct /r/ at some attempt (also after a cue or model). */
  correct: number;
  /** Targets correct at the very first attempt, before any model or cue. */
  firstTry: number;
  /** Judged attempts (practice, rewarded whatever the verdict). */
  attempts?: number;
  /** Longest run of accepted attempts. */
  bestStreak?: number;
}

export interface FloorProgress {
  /** Best stars earned on the floor. */
  stars: Stars;
  /** Missions finished on the floor. */
  completions: number;
  /** Codename letters uncovered so far. */
  revealed: number;
  /** Most words said with a correct /r/ in one mission: the child's own best to improve on. */
  bestCorrect: number;
  /** Judged attempts on this floor over all missions (practice counts). */
  practised?: number;
  /** Mastery estimate for the floor's /r/ (mastery.ts); absent until practised. */
  mastery?: Mastery;
  /**
   * Bonus stars: a clear r when transmitting the codename (epic #44). Counted apart from the
   * 1-3 mission stars, which keep their meaning (they open floors).
   */
  bonusStars?: number;
}

export type Progress = Readonly<Record<string, FloorProgress>>;

/** Share of targets correct for 2 stars, and for 3 stars (the clinical 80% criterion). */
export const TWO_STARS = 0.5;
export const THREE_STARS = 0.8;
/** Share of targets correct at the first try also needed for 3 stars. */
export const THREE_STARS_FIRST_TRY = 0.5;
/** Stars that open the next floor and uncover the whole codename. */
export const PASS_STARS = 2;

export const EMPTY: FloorProgress = { stars: 0, completions: 0, revealed: 0, bestCorrect: 0 };

export function starsFor(result: MissionResult): Stars {
  if (result.targets <= 0) return 1;
  const correct = result.correct / result.targets;
  const firstTry = result.firstTry / result.targets;
  if (correct >= THREE_STARS && firstTry >= THREE_STARS_FIRST_TRY) return 3;
  if (correct >= TWO_STARS) return 2;
  return 1;
}

export function floorProgress(progress: Progress, floorId: string): FloorProgress {
  return progress[floorId] ?? EMPTY;
}

/** Floors in order; the first is always open, each next one opens with 2 stars below it. */
export function isUnlocked(
  floorIds: readonly string[],
  index: number,
  progress: Progress,
): boolean {
  if (index <= 0) return true;
  const below = floorProgress(progress, floorIds[index - 1] ?? '');
  return below.stars >= PASS_STARS;
}

/**
 * Letters a mission may uncover: the whole codename only once the floor has 2 stars; until then
 * the last letter stays hidden, however many words were right.
 */
export function revealCap(stars: number, revealed: number, letters: number): number {
  return stars >= PASS_STARS ? revealed : Math.min(revealed, Math.max(0, letters - 1));
}

/**
 * Records a finished mission: best stars kept, completions counted, letters never lost; 2 or more
 * stars uncover the whole codename (`letters` long).
 */
export function recordMission(
  progress: Progress,
  floorId: string,
  result: MissionResult,
  revealed: number,
  letters: number,
): {
  progress: Progress;
  stars: Stars;
  improved: boolean;
  /** The child's best before this mission (words with a correct r), or null the first time. */
  previousBest: number | null;
} {
  const before = floorProgress(progress, floorId);
  const stars = starsFor(result);
  const best = Math.max(before.stars, stars) as Stars;
  const next: FloorProgress = {
    stars: best,
    completions: before.completions + 1,
    revealed:
      best >= PASS_STARS ? letters : revealCap(best, Math.max(before.revealed, revealed), letters),
    bestCorrect: Math.max(before.bestCorrect, result.correct),
    practised: (before.practised ?? 0) + (result.attempts ?? 0),
    ...(before.mastery ? { mastery: before.mastery } : {}),
  };
  return {
    progress: { ...progress, [floorId]: next },
    stars,
    improved: stars > before.stars,
    previousBest: before.completions > 0 ? before.bestCorrect : null,
  };
}

/** One judged attempt on a floor updates its mastery estimate. */
export function recordAttempt(
  progress: Progress,
  floorId: string,
  accepted: boolean,
  now: number,
  young: boolean,
): Progress {
  const before = floorProgress(progress, floorId);
  const mastery = updateMastery(before.mastery ?? newMastery(now), accepted, now, young);
  return { ...progress, [floorId]: { ...before, mastery } };
}

function validMastery(m: unknown): m is Mastery {
  if (!m || typeof m !== 'object') return false;
  const { p, attempts, updatedAt } = m as Partial<Mastery>;
  return (
    typeof p === 'number' &&
    p >= 0 &&
    p <= 1 &&
    typeof attempts === 'number' &&
    typeof updatedAt === 'number'
  );
}

/** Updates revealed letters during a mission (so leaving early keeps them). */
export function recordRevealed(progress: Progress, floorId: string, revealed: number): Progress {
  const before = floorProgress(progress, floorId);
  if (revealed <= before.revealed) return progress;
  return { ...progress, [floorId]: { ...before, revealed } };
}

const KEY = 'foe-names.progress.v1';

/** Progress stored on this device (never sent anywhere). Survives a missing or blocked store. */
export function loadProgress(
  storage: Pick<Storage, 'getItem'> | undefined = safeStorage(),
): Progress {
  try {
    const raw = storage?.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, Partial<FloorProgress>>;
    const out: Record<string, FloorProgress> = {};
    for (const [id, p] of Object.entries(parsed)) {
      out[id] = {
        stars: Math.min(3, Math.max(0, Math.round(Number(p.stars) || 0))) as Stars,
        completions: Math.max(0, Math.round(Number(p.completions) || 0)),
        revealed: Math.max(0, Math.round(Number(p.revealed) || 0)),
        bestCorrect: Math.max(0, Math.round(Number(p.bestCorrect) || 0)),
        ...(Number(p.practised) > 0 ? { practised: Math.round(Number(p.practised)) } : {}),
        ...(validMastery(p.mastery) ? { mastery: p.mastery } : {}),
        ...(Number(p.bonusStars) > 0 ? { bonusStars: Math.round(Number(p.bonusStars)) } : {}),
      };
    }
    return out;
  } catch {
    return {};
  }
}

export function saveProgress(
  progress: Progress,
  storage: Pick<Storage, 'setItem'> | undefined = safeStorage(),
): void {
  try {
    storage?.setItem(KEY, JSON.stringify(progress));
  } catch {
    // Storage full or blocked: progress lasts for this visit only.
  }
}

function safeStorage(): Storage | undefined {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage;
  } catch {
    return undefined;
  }
}

/** A clear r when transmitting the codename: one more bonus star on the floor. */
export function recordBonus(progress: Progress, floorId: string): Progress {
  const floor = floorProgress(progress, floorId);
  return { ...progress, [floorId]: { ...floor, bonusStars: (floor.bonusStars ?? 0) + 1 } };
}
